import { useState, useEffect, useCallback } from "react";

const AI_POLICY_KEY = "freedailypro-ai-policy";

export type AiPolicy = "unset" | "no-ai" | "copilot-only" | "most-ai-allowed";

/**
 * Remembers whether someone wants AI-powered features shown at all, and
 * (later) which kind. Entirely local to the browser -- nothing is sent
 * anywhere. No AI features exist yet, so nothing is actually gated by this
 * today; this hook exists so that when the first AI tool ships, it can
 * immediately respect a preference that's already been set, instead of
 * every AI feature needing this logic retrofitted individually.
 *
 * "no-ai" and "copilot-only" both mean: hide/de-emphasize AI features.
 * The distinction is kept for messaging purposes (a Copilot-only user gets
 * slightly different copy than a fully-restricted one), not for behavior --
 * both are treated as Safe Mode today.
 */
export function useAiPolicy() {
  const [policy, setPolicyState] = useState<AiPolicy>("unset");

  useEffect(() => {
    const stored = localStorage.getItem(AI_POLICY_KEY);
    if (
      stored === "no-ai" ||
      stored === "copilot-only" ||
      stored === "most-ai-allowed"
    ) {
      setPolicyState(stored);
    }
  }, []);

  const setPolicy = useCallback((next: AiPolicy) => {
    setPolicyState(next);
    if (next === "unset") {
      localStorage.removeItem(AI_POLICY_KEY);
    } else {
      localStorage.setItem(AI_POLICY_KEY, next);
    }
  }, []);

  const safeMode = policy === "no-ai" || policy === "copilot-only";

  return { policy, setPolicy, safeMode };
}
